import { useMutation } from "@tanstack/react-query";
import httpClient from "../httpClient";
import { HttpError } from "../httpError";
import { getCorrelationId } from "../../utils/getCorrelationId";

const ENDPOINT = "payment/pay";

interface PaymentPayload {
  amount: number | string;
  // The one service identifier the harmonized route binds (D-HARM-13). A
  // string, in the body, and required -- the integer row id is accepted
  // nowhere.
  core_service_id: string;
  payment_method_id: number | string;
  // ⚠ RETIRED by D-HARM-4 / D-HARM-14 / D-HARM-15 and STILL SENT BY DEPOSIT,
  // on purpose. The old server requires all three; the new one binds named
  // keys and ignores what it does not name, so one body satisfies both and the
  // deposit change could ship before the backend. They are removed for deposit
  // in the cleanup PR, where their absence is what gets proven.
  //
  // ⛔ OPTIONAL since 12-08, and the school-fee journey does NOT send them.
  // The route it replaces -- POST /schoolfee/payment -- was DELETED by
  // D-HARM-17, so school fee has no older contract to stay compatible with and
  // no reason to carry a field the harmonized one does not name. Its suite
  // asserts each is absent; the deposit suite asserts each is still present.
  corporate_subsidiary_id?: number | string;
  corporate_service_id?: number | string;
  channel?: string;
  instrument_account_owner?: string;
  instrument_account_number?: string;
  notification_email?: string;
  customer_id?: number;
  customer_wallet_id?: number;
  // The school-fee identity fragment. Which of these a payer must supply is a
  // property of the SERVICE, derived server-side from its type, variant and
  // searchability -- the client never declares the journey (D-HARM-7).
  //
  // The searched journey sends handler_id + student_uuid, validated against
  // one another (D-HARM-6); the two payer-declared journeys send selection_id
  // and a typed identity instead. A payload that mixes them is refused before
  // any resolution or charge.
  handler_id?: string;
  student_uuid?: string;
  selection_id?: string;
  matricule?: string;
  first_name?: string;
  last_name?: string;
  date_of_birth?: string;
  // Exactly one in V1. handler_id resolves who the pupil is and what they owe;
  // WHICH of those the payer chose is a choice, not a fact, and a server
  // cannot infer a choice.
  fee_assignment_ids?: string[];
  // The payer's answers to a service's configured questions, keyed by the
  // field id the service declared. Omitted when it asked nothing.
  cdata?: Record<string, string>;
}

// The spec's InstrumentError (D-HARM-8): what CPS forwards on a declined
// payment. Declared `string | null` this field once made the old
// `typeof response.error === "string"` guard permanently false, so a real
// decline rendered a hardcoded literal.
//
// customerMsg stays `unknown` and the map stays open on purpose: the server
// side of D-HARM-8 types deposit's bare `*map[string]any`, stored rows keep
// whichever shape they were written with, and a client stricter than the
// server's own output is a new failure mode rather than a safer one.
export type InstrumentError = {
  errorCode?: string;
  customerMsg?: unknown;
  devMsg?: string;
  [key: string]: unknown;
};

export interface PayResponse {
  payment_item_id: string;
  amount: number;
  // The unit of every monetary value on this response, so a consumer never has
  // to assume one. Deposit has never carried it; D-HARM-9 seats it.
  currency: string;
  // The charge split, always present and long returned but never declared
  // here. ⚠ It is a re-fetched pre-pay quote, not the settled charge -- it is
  // not put on a receipt and not described to a payer as what they were
  // charged.
  total_amount: number;
  instrument_fee: number;
  registration_fee: number;
  total_fees: number;
  status: string;
  processed_at: string;
  error: InstrumentError | string | null;
}

// The reference the request carried travels out with the result, so the caller
// shows the payer the same value support will search on (D-17).
export type PayResult = PayResponse & { reference: string };

const isValidPayResponse = (response: unknown): response is PayResponse => {
  if (typeof response !== "object" || response === null) return false;

  const candidate = response as Record<string, unknown>;

  return (
    typeof candidate.payment_item_id === "string" &&
    typeof candidate.status === "string" &&
    // Tightened by D-HARM-9: the spec's `required` is exactly
    // [payment_item_id, amount, currency, status], so a response without a
    // currency is a broken contract rather than an old server.
    typeof candidate.currency === "string" &&
    // ⚠ Deliberately LEFT stricter than the contract, which does not require
    // it and types it nullable. Measured: the handler sets ProcessedAt at both
    // construction sites, so the gap is contract-vs-implementation and not a
    // live break; loosening the guard on no evidence is the larger risk.
    // Pinned by its own test and filed as TODO-265.
    typeof candidate.processed_at === "string" &&
    "error" in candidate
  );
};

export const usePayMutation = ({
  authorisation = "",
}: {
  authorisation: string;
}) =>
  useMutation<PayResult, HttpError, PaymentPayload>(async payload => {
    const reference = getCorrelationId();

    const response = await httpClient(ENDPOINT, {
      authorisation,
      correlationId: reference,
    }).post<unknown>(payload);

    if (!isValidPayResponse(response)) {
      throw new HttpError({
        kind: "parse",
        message: "The payment response did not match the contract",
        url: ENDPOINT,
        correlationId: reference,
      });
    }

    // A decline with an id is a created payment whose outcome page polls and
    // explains it, so it resolves. With no id nothing was created; the envelope
    // stays on `body` for the resolver rather than becoming an English literal.
    if (response.error && response.payment_item_id === "") {
      const error =
        typeof response.error === "string" ? undefined : response.error;

      throw new HttpError({
        kind: "http",
        message: `Payment declined by ${ENDPOINT}`,
        url: ENDPOINT,
        status: 200,
        code: typeof error?.errorCode === "string" ? error.errorCode : undefined,
        body: response.error,
        correlationId: reference,
      });
    }

    return { ...response, reference };
  });

export default usePayMutation;
